import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import { SkeletonBar, tokens } from 'components/v3';
import { fieldErrorsOf } from 'shared/apolloFieldErrors';
import { formatDateTime } from '../../Forms/shared/dates';
import Glyph from '../../Forms/Submissions/Glyph';
import { ORANGE, sx } from '../../Forms/Submissions/sx';
import { useGetFormSubmissionEvents } from '../gql';

// The entry card's "Access log" section (design W9Parts.W9Log, w9-plan
// T10, §6.3 item 9): every signed / pdf / reveal / edit event on this
// W-9, newest first. `refreshKey` changing refetches (after a reveal or a
// PDF download from the same card).

export const EVENT_ICON = {
  signed: { icon: 'pen', tone: 'ok' },
  pdf: { icon: 'download', tone: 'plain' },
  reveal: { icon: 'eye', tone: 'brand' },
  edit: { icon: 'pencil', tone: 'brand' },
};

// → { icon, tone, title, by, extra }
export const describeEvent = (event, submitterName) => {
  const meta = (event && event.meta) || {};
  const look = EVENT_ICON[event.kind] || { icon: 'info', tone: 'plain' };
  const staff = event.user_name || 'Your team';
  switch (event.kind) {
    case 'signed':
      return {
        ...look,
        title: 'Submitted and e-signed',
        by: event.user_name || submitterName || 'The driver',
        extra: meta.ip ? `IP ${meta.ip}` : null,
      };
    case 'pdf':
      return { ...look, title: 'W-9 PDF downloaded', by: staff, extra: null };
    case 'reveal':
      return { ...look, title: 'Full TIN shown', by: staff, extra: null };
    case 'edit': {
      const fields = Array.isArray(meta.fields) ? meta.fields : [];
      return {
        ...look,
        title: fields.length
          ? `${fields.join(', ')} edited`
          : 'Payee details edited',
        by: staff,
        extra: null,
      };
    }
    default:
      return {
        ...look,
        title: String(event.kind || 'Activity'),
        by: staff,
        extra: null,
      };
  }
};

export const sortEvents = (events) =>
  [...(events || [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

const Section = styled.section`
  margin-top: ${(p) => (p.$mobile ? '20px' : '24px')};
`;

export const SectionLabel = styled.h3`
  margin: 0 0 10px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  color: ${(p) => sx(p.theme).otext};
`;

const List = styled.div`
  border: 1px solid ${(p) => sx(p.theme).line};
  border-radius: 12px;
  background: ${(p) => sx(p.theme).card};
  overflow: hidden;
`;

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;

  & + & {
    border-top: 1px solid ${(p) => sx(p.theme).line};
  }
`;

const Tile = styled.span`
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) =>
    p.$tone === 'ok'
      ? sx(p.theme).okBg
      : p.$tone === 'brand'
      ? sx(p.theme).tint
      : sx(p.theme).fill};
  color: ${(p) =>
    p.$tone === 'ok'
      ? sx(p.theme).ok
      : p.$tone === 'brand'
      ? ORANGE
      : sx(p.theme).text2};
`;

const AccessLog = ({ submissionId, submitterName, refreshKey, isMobile }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const { events, loading, error, refetch } =
    useGetFormSubmissionEvents(submissionId);
  useEffect(() => {
    if (refreshKey && typeof refetch === 'function') {
      Promise.resolve(refetch()).catch(() => undefined);
    }
  }, [refreshKey, refetch]);

  let body;
  if (error && !events.length && !loading) {
    body = (
      <div
        role="alert"
        style={{ padding: '12px 14px', fontSize: 13, color: t.sub }}
      >
        Couldn&apos;t load the access log. {fieldErrorsOf(error).global}{' '}
        <ButtonV2
          onClick={() => Promise.resolve(refetch()).catch(() => undefined)}
          style={{ width: 'auto', marginTop: 8 }}
        >
          Retry
        </ButtonV2>
      </div>
    );
  } else if (loading && !events.length) {
    body = (
      <div aria-busy="true" style={{ padding: 14, display: 'grid', gap: 10 }}>
        <SkeletonBar $w="60%" $h="14px" />
        <SkeletonBar $w="40%" $h="12px" />
      </div>
    );
  } else if (!events.length) {
    body = (
      <div style={{ padding: '12px 14px', fontSize: 13, color: t.faint }}>
        No activity recorded yet.
      </div>
    );
  } else {
    body = sortEvents(events).map((event) => {
      const d = describeEvent(event, submitterName);
      return (
        <Item key={event.id} data-w9-event={event.kind}>
          <Tile $tone={d.tone} aria-hidden="true">
            <Glyph name={d.icon} size={14} />
          </Tile>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: t.text }}>
              {d.title}
            </div>
            <div
              style={{
                marginTop: 2,
                fontSize: 12,
                lineHeight: 1.4,
                color: t.faint,
              }}
            >
              {d.by} · {formatDateTime(event.created_at)}
              {d.extra ? ` · ${d.extra}` : ''}
            </div>
          </div>
        </Item>
      );
    });
  }

  return (
    <Section $mobile={isMobile} aria-label="Access log" data-w9-access-log="">
      <SectionLabel>Access log</SectionLabel>
      <List>{body}</List>
    </Section>
  );
};

AccessLog.propTypes = {
  submissionId: PropTypes.number.isRequired,
  submitterName: PropTypes.string,
  refreshKey: PropTypes.number,
  isMobile: PropTypes.bool,
};

AccessLog.defaultProps = {
  submitterName: null,
  refreshKey: 0,
  isMobile: false,
};

export default AccessLog;
